import { useRef } from "react";
import { toPng } from "html-to-image";
import { Download, Printer, X } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { fileUrl } from "../lib/api";
import { LOGO } from "../lib/constants";
import { toast } from "sonner";

const KIND_LABEL = {
  tip: "Tip", pledge: "Monthly pledge", jar_contribution: "Community jar contribution",
  jar_split: "Community jar share", cashout: "Cash-out", shop: "Shop purchase",
};

export default function ReceiptModal({ txn, onClose }) {
  const ref = useRef();
  if (!txn) return null;

  const inbound = txn.direction === "in";
  const dt = new Date(txn.created_at);
  const receiptNo = "HP-" + (txn.ref || "").replace(/[^a-zA-Z0-9]/g, "").slice(-10).toUpperCase();

  const download = async () => {
    if (!ref.current) return;
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, backgroundColor: "#fffaf5" });
      const a = document.createElement("a");
      a.href = url; a.download = `${receiptNo}.png`; a.click();
    } catch { toast.error("Couldn't generate the image. Try Print instead."); }
  };

  const print = async () => {
    if (!ref.current) return;
    const url = await toPng(ref.current, { pixelRatio: 2, backgroundColor: "#fffaf5" });
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<img src="${url}" style="width:100%" onload="window.print();window.close()" />`);
    w.document.close();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="receipt-modal" className="sm:max-w-md rounded-3xl p-0 overflow-hidden">
        <div ref={ref} className="p-6 bg-[#fffaf5]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={LOGO} alt="" className="w-9 h-9 rounded-lg" />
              <span className="font-serif font-bold text-bronze text-lg">HAPLAP</span>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${inbound ? "bg-sage/15 text-sage" : "bg-copper/15 text-copper"}`}>
              {inbound ? "Received" : "Given"}
            </span>
          </div>

          <p className="font-mono text-[11px] text-muted-foreground mt-4">Receipt {receiptNo}</p>
          <div className="text-center py-5 border-y border-dashed border-border my-3">
            <p className="text-xs text-muted-foreground">{KIND_LABEL[txn.kind] || txn.kind}</p>
            <p className={`font-serif text-4xl font-bold tabular-nums ${inbound ? "text-sage" : "text-bronze"}`}>
              {inbound ? "+" : "−"}${txn.amount.toFixed(2)}
            </p>
            {txn.recurring && <p className="text-[11px] text-copper mt-1">Recurring monthly 💞</p>}
          </div>

          <dl className="text-sm space-y-2">
            {txn.counterparty && (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{inbound ? "From" : "To"}</dt>
                <dd className="font-medium text-right flex items-center gap-1.5">
                  <img src={fileUrl(txn.counterparty.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${txn.counterparty.user_id}`} alt="" className="w-5 h-5 rounded-full bg-secondary" />
                  {txn.counterparty.name}
                </dd>
              </div>
            )}
            {txn.anonymous && <div className="flex justify-between"><dt className="text-muted-foreground">From</dt><dd className="font-medium">Anonymous friend</dd></div>}
            {txn.item && <div className="flex justify-between"><dt className="text-muted-foreground">Item</dt><dd className="font-medium">{txn.item.emoji} {txn.item.name}</dd></div>}
            <div className="flex justify-between"><dt className="text-muted-foreground">Date</dt><dd className="font-medium">{dt.toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Status</dt><dd className="font-medium capitalize text-sage">{txn.status}</dd></div>
            {txn.message && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Note</dt><dd className="font-medium text-right italic">"{txn.message}"</dd></div>}
          </dl>

          {txn.fees && txn.fees.total_charged != null && (
            <div className="mt-3 pt-3 border-t border-dashed border-border text-sm space-y-1.5">
              <p className="text-[11px] font-mono uppercase tracking-widest text-copper">Fee breakdown (you covered)</p>
              <div className="flex justify-between text-muted-foreground"><span>Donation</span><span>${txn.amount.toFixed(2)}</span></div>
              {txn.fees.platform_fee != null && <div className="flex justify-between text-muted-foreground"><span>Platform fee (5%)</span><span>${Number(txn.fees.platform_fee).toFixed(2)}</span></div>}
              {txn.fees.stripe_fee != null && <div className="flex justify-between text-muted-foreground"><span>Processing</span><span>${Number(txn.fees.stripe_fee).toFixed(2)}</span></div>}
              <div className="flex justify-between font-semibold text-bronze"><span>Total charged</span><span>${Number(txn.fees.total_charged).toFixed(2)}</span></div>
            </div>
          )}

          <p className="text-center text-[10px] text-muted-foreground mt-5 font-serif italic">Thank you for being part of HAPLAP 💛</p>
        </div>

        <div className="flex gap-2 p-4 border-t border-border bg-card">
          <Button data-testid="receipt-download" onClick={download} className="flex-1 rounded-full bg-copper hover:bg-copper/90 text-white"><Download className="w-4 h-4 mr-1.5" /> PNG</Button>
          <Button data-testid="receipt-print" onClick={print} variant="outline" className="flex-1 rounded-full"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
          <Button onClick={onClose} variant="ghost" size="icon" className="rounded-full"><X className="w-4 h-4" /></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
